import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
} from '@mui/material';
import {
  Store,
  LocationOn,
  Navigation,
  Verified,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Schedule,
  History,
  FilterList,
  MyLocation,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLocation as useLocationContext, useLocationDialog } from '../contexts/LocationContext';
import { url } from '../constants';

// ✅ Helper to securely load vendor profile images
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('data:image') || imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${url}${imagePath}`;
};

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('top');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { userLocation } = useLocationContext();
  const { openLocationDialog } = useLocationDialog();
  const observerTarget = useRef(null);
  const categoryScrollRef = useRef(null);

  // ✅ LOAD CATEGORIES FROM LOCALSTORAGE (CategorySection style)
  useEffect(() => {
    const savedCategories = localStorage.getItem('categories');
    if (savedCategories) {
      try {
        const parsedCategories = JSON.parse(savedCategories);
        if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
          console.log('💾 Loaded categories from localStorage:', parsedCategories.length);
          setCategories(parsedCategories);
          return;
        }
      } catch (error) {
        console.error('❌ Error parsing categories:', error);
        localStorage.removeItem('categories');
      }
    }
    fetchCategories();
  }, []);

 // ✅ FIXED - Handles numbers + strings safely
const getVendorGradient = (vendor, index = 0) => {
  // 1. Safely get vendor category (handle numbers/undefined)
  const vendorCategory = vendor.categories?.[0] || vendor.business_type;
  
  // 2. Find matching category in localStorage (FIXED matching logic)
  const categoryMatch = categories.find(cat => {
    // Handle number vs string matching safely
    const vendorCatStr = String(vendorCategory || '').toLowerCase();
    const catNameStr = String(cat.cate_name || '').toLowerCase();
    const catIdStr = String(cat.cate_id || '').toLowerCase();
    
    return catIdStr === vendorCatStr || 
           catNameStr.includes(vendorCatStr) || 
           vendorCatStr.includes(catNameStr);
  });
  
  // 3. Use stored category color OR business_type fallback
  let baseColor = categoryMatch?.cate_color;
  if (!baseColor) {
    const typeColors = {
      'Grocery': '#4CAF50',
      'Restaurant': '#FF5722', 
      'Electronics': '#2196F3',
      'Fashion': '#9C27B0',
      'Pharmacy': '#FF9800',
      'Salon': '#E91E63',
      'Cafe': '#795548',
      'Jewelry': '#FFD700',
      'Bakery': '#FFEB3B',
      'Stationery': '#607D8B',
      'General': '#1a7f3a',
    };
    baseColor = typeColors[vendor.business_type] || '#1a7f3a';
  }

  // 4. Generate gradient (unchanged)
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const gradientStyles = [
    `linear-gradient(135deg, rgb(${Math.floor(r*0.9)},${Math.floor(g*0.9)},${Math.floor(b*0.9)}) 0%, rgb(${Math.floor(r*0.7)},${Math.floor(g*0.7)},${Math.floor(b*0.7)}) 50%, rgb(${Math.floor(r*0.5)},${Math.floor(g*0.5)},${Math.floor(b*0.5)}) 100%)`,
    `radial-gradient(circle at 30% 30%, rgb(${Math.floor(r*0.95)},${Math.floor(g*0.95)},${Math.floor(b*0.95)}) 0%, rgb(${Math.floor(r*0.75)},${Math.floor(g*0.75)},${Math.floor(b*0.75)}) 50%, rgb(${Math.floor(r*0.55)},${Math.floor(g*0.55)},${Math.floor(b*0.55)}) 100%)`,
    `linear-gradient(180deg, rgb(${Math.floor(r*0.9)},${Math.floor(g*0.9)},${Math.floor(b*0.9)}) 0%, rgb(${Math.floor(r*0.6)},${Math.floor(g*0.6)},${Math.floor(b*0.6)}) 100%)`,
  ];
  
  return gradientStyles[index % gradientStyles.length];
};

  // Fetch categories API fallback
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${url}/users/get_categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      
      const categoriesWithTimestamp = data.map(category => ({
        ...category,
        cachedAt: Date.now()
      }));
      
      localStorage.setItem('categories', JSON.stringify(categoriesWithTimestamp));
      setCategories(categoriesWithTimestamp);
      console.log('✅ Categories saved to localStorage:', data.length);
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
    }
  };

  // Reset when filter or category changes
  useEffect(() => {
    setVendors([]);
    setPage(1);
    setHasMore(true);
    setError('');
    
    if (filter !== 'nearby' || (filter === 'nearby' && userLocation)) {
      fetchVendors(1, true);
    }
  }, [filter, selectedCategory, userLocation]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          console.log('📜 Loading more vendors...');
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadingMore]);

  // Load more when page changes
  useEffect(() => {
    if (page > 1) {
      fetchVendors(page, false);
    }
  }, [page]);

  // Check scroll arrows visibility
  useEffect(() => {
    const checkArrows = () => {
      if (categoryScrollRef.current && !isMobile) {
        const { scrollLeft, scrollWidth, clientWidth } = categoryScrollRef.current;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    checkArrows();
    const scrollEl = categoryScrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('scroll', checkArrows);
      window.addEventListener('resize', checkArrows);
    }

    return () => {
      if (scrollEl) {
        scrollEl.removeEventListener('scroll', checkArrows);
      }
      window.removeEventListener('resize', checkArrows);
    };
  }, [categories, isMobile]);

 // ✅ Add this Haversine distance function at top of Vendors component
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return (R * c).toFixed(1);
};

// ✅ UPDATED fetchVendors - Calculate distance for ALL vendors
const fetchVendors = async (currentPage, reset = false) => {
  if (reset) {
    setLoading(true);
  } else {
    setLoadingMore(true);
  }
  setError('');

  try {
    let endpoint = `${url}/vendors?filter=${filter}&page=${currentPage}&page_size=20`;

    if (filter === 'nearby' && userLocation) {
      endpoint += `&lat=${userLocation.lat}&lng=${userLocation.lng}&radius_km=50`;
    }

    if (selectedCategory) {
      endpoint += `&category=${selectedCategory}`;
    }

    console.log('📍 Fetching vendors:', endpoint);

    const response = await fetch(endpoint);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: Failed to fetch vendors`);
    }

    const data = await response.json();
    console.log(`✅ Fetched ${data.vendors.length} vendors (page ${currentPage})`);
    
    // ✅ Calculate distance for ALL vendors using userLocation
    const vendorsWithDistance = data.vendors.map(vendor => {
      if (userLocation && vendor.location?.coordinates) {
        const [lon, lat] = vendor.location.coordinates; // MongoDB: [lng, lat]
        const distance = calculateDistance(
          userLocation.lat, userLocation.lng,
          lat, lon
        );
        return { ...vendor, distance };
      }
      return { ...vendor, distance: null };
    });
    
    setTotal(data.total);
    setHasMore(data.has_more);
    
    if (reset) {
      setVendors(vendorsWithDistance);
    } else {
      setVendors((prev) => [...prev, ...vendorsWithDistance]);
    }
  } catch (error) {
    console.error('❌ Error fetching vendors:', error);
    setError(error.message || 'Failed to load vendors');
    setHasMore(false);
  } finally {
    setLoading(false);
    setLoadingMore(false);
  }
};


  const handleFilterChange = (event) => {
    const newFilter = event.target.value;
    if (newFilter !== filter) {
      setFilter(newFilter);
    }
  };

  const handleCategoryClick = (categoryId) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(categoryId);
    }
  };

  const handleVendorClick = (vendorId) => {
    navigate(`/vendor/${vendorId}`);
  };

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200;
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', py: isMobile ? 2 : 6 }}>
      <Container maxWidth="lg" sx={{ px: isMobile ? 0 : 2 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: isMobile ? 2 : 3,
          px: isMobile ? 2 : 0,
        }}>
          <Box>
            <Typography 
              variant={isMobile ? 'h5' : 'h4'} 
              gutterBottom 
              fontWeight={700} 
              sx={{ color: '#4B5563' }}
            >
              Vendors Near You
            </Typography>
            <Typography variant={isMobile ? 'caption' : 'body1'} color="text.secondary">
              {total > 0 ? `${total} vendor${total !== 1 ? 's' : ''} found` : 'Discover amazing local businesses'}
            </Typography>
          </Box>
        </Box>

        {/* Filter Dropdown & Category Chips */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1.5 : 2, 
          mb: isMobile ? 2 : 4, 
          px: isMobile ? 2 : 0,
          alignItems: isMobile ? 'stretch' : 'center',
        }}>
          {/* Filter Dropdown */}
          <FormControl size={isMobile ? 'small' : 'medium'} sx={{ minWidth: isMobile ? '100%' : 180, flexShrink: 0 }}>
            <Select
              value={filter}
              onChange={handleFilterChange}
              sx={{
                bgcolor: 'white',
                borderRadius: 1,
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 600,
                color: '#4B5563',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1a7f3a',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0f5328',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1a7f3a',
                },
              }}
            >
              <MenuItem value="top" sx={{ fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                <TrendingUp sx={{ mr: 1, fontSize: 16 }} /> Top Rated
              </MenuItem>
              <MenuItem value="nearby" sx={{ fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                <Navigation sx={{ mr: 1, fontSize: 16 }} /> Nearby
              </MenuItem>
              <MenuItem value="latest" sx={{ fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                <Schedule sx={{ mr: 1, fontSize: 16 }} /> Latest
              </MenuItem>
              <MenuItem value="oldest" sx={{ fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                <History sx={{ mr: 1, fontSize: 16 }} /> Oldest
              </MenuItem>
            </Select>
          </FormControl>

          {/* Category Chips */}
          <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
            {!isMobile && showLeftArrow && (
              <IconButton
                onClick={() => scrollCategories('left')}
                sx={{
                  position: 'absolute',
                  left: -16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 2,
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: '#f0f0f0' },
                }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
            )}

            <Box
              ref={categoryScrollRef}
              sx={{
                display: 'flex',
                gap: isMobile ? 0.6 : 1,
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                '&::-webkit-scrollbar': { height: 0, display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              <Chip
                label="All"
                onClick={() => handleCategoryClick(null)}
                sx={{
                  bgcolor: !selectedCategory ? '#1a7f3a' : 'white',
                  color: !selectedCategory ? 'white' : '#4B5563',
                  fontWeight: 600,
                  px: isMobile ? 0.8 : 1.5,
                  py: isMobile ? 1.5 : 2,
                  fontSize: isMobile ? '0.7rem' : '0.8rem',
                  height: isMobile ? 28 : 36,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                  border: !selectedCategory ? 'none' : '1px solid #E0E0E0',
                  '&:hover': {
                    bgcolor: !selectedCategory ? '#0f5328' : '#f0f0f0',
                  },
                }}
              />

              {categories.map((category) => (
                <Chip
                  key={category.cate_id}
                  label={category.cate_name}
                  onClick={() => handleCategoryClick(category.cate_id)}
                  sx={{
                    bgcolor: selectedCategory === category.cate_id ? '#1a7f3a' : 'white',
                    color: selectedCategory === category.cate_id ? 'white' : '#4B5563',
                    fontWeight: 600,
                    px: isMobile ? 0.8 : 1.5,
                    py: isMobile ? 1.5 : 2,
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    height: isMobile ? 28 : 36,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    flexShrink: 0,
                    border: selectedCategory === category.cate_id ? 'none' : '1px solid #E0E0E0',
                    '&:hover': {
                      bgcolor: selectedCategory === category.cate_id ? '#0f5328' : '#f0f0f0',
                    },
                  }}
                />
              ))}
            </Box>

            {!isMobile && showRightArrow && (
              <IconButton
                onClick={() => scrollCategories('right')}
                sx={{
                  position: 'absolute',
                  right: -16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 2,
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: '#f0f0f0' },
                }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, mx: isMobile ? 2 : 0 }}>
            {error}
          </Alert>
        )}

        {/* Location Required */}
        {filter === 'nearby' && !userLocation && !loading && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8, 
            px: isMobile ? 2 : 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}>
            <MyLocation sx={{ fontSize: 80, color: '#1a7f3a' }} />
            <Typography variant="h6" color="text.primary" sx={{ mb: 1, fontWeight: 600 }}>
              Location Required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please select your location to see nearby vendors
            </Typography>
            <Button
              variant="contained"
              startIcon={<LocationOn />}
              onClick={openLocationDialog}
              sx={{
                bgcolor: '#1a7f3a',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.95rem',
                '&:hover': { bgcolor: '#0f5328' },
              }}
            >
              Select Location
            </Button>
          </Box>
        )}

        {/* Vendors Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress sx={{ color: '#1a7f3a' }} size={50} />
              <Typography variant="body1" color="text.secondary">
                Loading vendors...
              </Typography>
            </Stack>
          </Box>
        ) : (
          <>
            {(filter !== 'nearby' || userLocation) && (
              <>
                {vendors.length > 0 && (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'repeat(2, 1fr)',
                          sm: 'repeat(2, 1fr)',
                          md: 'repeat(3, 1fr)',
                          lg: 'repeat(4, 1fr)',
                        },
                        gap: isMobile ? 0 : 2,
                        mb: 4,
                      }}
                    >
                      {vendors.map((vendor, index) => (
                        <Card
                          key={`${vendor._id}-${index}`}
                          onClick={() => handleVendorClick(vendor._id)}
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: isMobile ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                            borderRadius: isMobile ? 0 : 1,
                            borderRight: isMobile ? '2px solid #E0E0E0' : 'none',
                            borderBottom: isMobile ? '1px solid #E0E0E0' : 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                              boxShadow: isMobile ? 'none' : '0 4px 12px rgba(0,0,0,0.15)',
                              transform: isMobile ? 'none' : 'translateY(-4px)',
                            },
                          }}
                        >
                          {/* ✅ Dynamic Image / Gradient Placeholder */}
                          <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                            {vendor.profile_image ? (
                              <CardMedia
                                component="img"
                                height={isMobile ? "140" : "180"}
                                image={getImageUrl(vendor.profile_image)}
                                alt={vendor.business_name}
                                sx={{
                                  objectFit: 'cover',
                                  userSelect: 'none',
                                  pointerEvents: 'none',
                                }}
                              />
                            ) : (
                              // Fallback Gradient if no image exists
                              <Box sx={{ 
                                height: isMobile ? '140px' : '180px',
                                background: getVendorGradient(vendor, index),
                                backgroundSize: '400% 400%',
                                animation: 'gradientShift 3s ease infinite',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Store sx={{ fontSize: 64, color: 'rgba(255,255,255,0.4)' }} />
                              </Box>
                            )}
                            
                            {/* Distance Chip */}
                            {vendor.distance && (
                              <Chip
                              icon={<Navigation />}
                              label={`${vendor.distance} km`}
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: isMobile ? 4 : 12,
                                left: isMobile ? 4 : 12,
                                fontWeight: 700,
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                fontSize: isMobile ? '0.65rem' : '0.7rem',
                                height: isMobile ? 20 : 24,
                                '& .MuiChip-icon': {
                                  color: 'white !important',
                                },
                              }}
                            />
                            )}
                            
                            {/* Verified Badge */}
                            {vendor.is_verified && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: isMobile ? 8 : 12,
                                  right: isMobile ? 8 : 12, // Moved to right to balance layout
                                  bgcolor: 'rgba(255,255,255,0.95)',
                                  borderRadius: '50%',
                                  p: isMobile ? 0.4 : 0.5,
                                  display: 'flex',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                  zIndex: 3,
                                }}
                              >
                                <Verified sx={{ fontSize: isMobile ? 12 : 16, color: '#1a7f3a' }} />
                              </Box>
                            )}
                          </Box>

                          <CardContent sx={{ p: isMobile ? 1 : 2, flexGrow: 1 }}>
                            <Stack direction="row" spacing={0.5} sx={{ mb: isMobile ? 0.5 : 1 }}>
                              <Chip
                                label={vendor.business_type}
                                size="small"
                                sx={{
                                  bgcolor: '#f0f0f0',
                                  color: '#1a7f3a',
                                  fontWeight: 600,
                                  fontSize: isMobile ? '0.6rem' : '0.7rem',
                                  height: isMobile ? 18 : 24,
                                }}
                              />
                            </Stack>

                            {/* ✅ Vendor Name moved here for perfect readability */}
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              sx={{
                                mb: 0.5,
                                color: '#1E293B',
                                fontSize: isMobile ? '0.95rem' : '1.1rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {vendor.business_name}
                            </Typography>

                            {vendor.phone && (
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                <Store sx={{ fontSize: 12, color: '#878787' }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#878787',
                                    fontSize: isMobile ? '0.6rem' : '0.7rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {vendor.phone}
                                </Typography>
                              </Stack>
                            )}

                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              sx={{ 
                                color: '#1a7f3a',
                                fontSize: isMobile ? '0.75rem' : '0.85rem',
                                mb: 0.5,
                              }}
                            >
                              {vendor.total_deals || 0} Active Deal{vendor.total_deals !== 1 ? 's' : ''}
                            </Typography>

                            <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ minHeight: 32 }}>
                              <LocationOn sx={{ fontSize: 14, color: '#1a7f3a', mt: 0.2, flexShrink: 0 }} />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#878787',
                                  fontSize: isMobile ? '0.6rem' : '0.7rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: 1.4,
                                }}
                              >
                                {vendor.address?.street}, {vendor.address?.city}, {vendor.address?.state} - {vendor.address?.pincode}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>

                    {loadingMore && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: '#1a7f3a' }} size={40} />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mt: 1 }}>
                          Loading more vendors...
                        </Typography>
                      </Box>
                    )}

                    {!hasMore && vendors.length > 0 && (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          You've reached the end • {total} vendor{total !== 1 ? 's' : ''} total
                        </Typography>
                      </Box>
                    )}

                    <div ref={observerTarget} style={{ height: '20px' }} />
                  </>
                )}

                {vendors.length === 0 && !error && (
                  <Box sx={{ textAlign: 'center', py: 8, px: isMobile ? 2 : 0 }}>
                    <Store sx={{ fontSize: 80, color: '#D1D5DB', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No vendors found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try changing the filter or category
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Container>

      {/* ✨ Gradient Animation CSS */}
      <style jsx>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </Box>
  );
};

export default Vendors;